import { useCallback } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { Stack } from '@mui/material';
import AppSidebarItem from '@common/ui/AppSidebarItem';
import { MaterialIcon, SidebarBadge, UpgradeWrapper } from '@components';
import { routes } from '@routes';
import { useFlagFixed, useIntegrationCatalogStatuses, useHasPermission } from '@hooks';
import { Flag, QueryParams } from '@utils';
import NavBarSearch from './NavBarSearch';
import { GetStartedNavIcon } from '@organisms';
import NewBadge from '@common/ui/NewBadge';
import { CustomAnalyticsEvents } from '@utils/analytics';
import DividerText from '@common/ui/DividerText/DividerText';
import { Permission } from '@common/utils/admin_permissions';

interface NavigationProps {
  minimized?: boolean;
  setMinimized: (value: boolean) => void;
}

interface SidebarItem {
  label: string;
  icon: JSX.Element;
  to: string;
  active: boolean;
  isEnabled: boolean;
  hasPermission?: boolean;
  dataTrigger?: string;
}

export default function Navigation({ minimized, setMinimized }: NavigationProps) {
  const location = useLocation();

  const flags = {
    templateSuggestion: useFlagFixed(Flag.OVERVIEW_TEMPLATE_SUGGESTION),
    activeAccess: useFlagFixed(Flag.NEW_UI_ACTIVE_ACCESS),
    accessGraph: useFlagFixed(Flag.APONO_ACCESS_GRAPH),
    bundles: useFlagFixed(Flag.UI_BUNDLES),
    sessionAudit: useFlagFixed(Flag.SESSION_AUDIT),
    webhooks: useFlagFixed(Flag.OUTBOUND_WEBHOOKS),
    accessRightSizing: useFlagFixed(Flag.ACCESS_RIGHT_SIZING),
    alerts: useFlagFixed(Flag.ACCESS_ANOMALIES),
    inventory: useFlagFixed(Flag.SHOW_INVENTORY),
    assessmentReports: useFlagFixed(Flag.ASSESSMENT_REPORTS),
    systemLog: useFlagFixed(Flag.SHOW_SYSLOG),
  };

  const permissions = {
    dashboard: useHasPermission([Permission.DASHBOARD_READ]),
    activity: useHasPermission([Permission.ACTIVITY_READ]),
    integrations: useHasPermission([Permission.INTEGRATIONS_READ]),
    connectors: useHasPermission([Permission.CONNECTORS_READ]),
    identities: useHasPermission([Permission.IDENTITIES_READ]),
    reports: useHasPermission([Permission.REPORTS_READ]),
    accessGraph: useHasPermission([Permission.ACCESS_GRAPH_READ]),
    anomalies: useHasPermission([Permission.ANOMALIES_READ]),
    accessFlow: useHasPermission([Permission.ACCESS_FLOWS_READ]),
    inventory: useHasPermission([Permission.INVENTORY_READ]),
    standingAccess: useHasPermission([Permission.JIT_PROTECT_READ]),
    syslog: useHasPermission([Permission.SYSLOG_READ]),
    webhook: useHasPermission([Permission.WEBHOOK_READ]),
    sessionAudit: useHasPermission([Permission.SESSION_AUDIT_READ]),
    accessBundle: useHasPermission([Permission.ACCESS_BUNDLES_READ]),
    accessRightSizing: useHasPermission([Permission.RIGHT_SIZING_READ]),
    search: useHasPermission([Permission.IDENTITIES_READ, Permission.ACCESS_FLOWS_READ, Permission.INTEGRATIONS_READ]),
  };

  const isActive = useCallback(
    (prefix: string | string[]) => {
      const prefixes = Array.isArray(prefix) ? prefix : [prefix];
      return prefixes.some((p) => matchPath({ path: p, end: p === '/' }, location.pathname));
    },
    [location],
  );

  const overviewItems: SidebarItem[] = [
    {
      label: 'Dashboard',
      icon: <GetStartedNavIcon testId="onboarding-badge" icon={<MaterialIcon symbol="view_quilt" weight={800} />} />,
      to: routes.Dashboard.path,
      active: isActive(routes.Dashboard.path),
      isEnabled: flags.templateSuggestion.isEnabled,
      hasPermission: permissions.dashboard,
    },
    {
      label: 'Right Sizing',
      icon: (
        <UpgradeWrapper isPremium={flags.accessRightSizing.isPremium} withPopover={false}>
          <MaterialIcon symbol="vertical_align_center" weight={800} />
        </UpgradeWrapper>
      ),
      to: routes.AccessRightSizing.path,
      active: isActive(routes.AccessRightSizing.path),
      isEnabled: flags.accessRightSizing.isEnabled,
      hasPermission: permissions.accessRightSizing,
      dataTrigger: CustomAnalyticsEvents.ACCESS_RIGHT_SIZING_VISIT_PAGE,
    },
    {
      label: 'Access Graph',
      icon: (
        <UpgradeWrapper isPremium={flags.accessGraph.isPremium} withPopover={false}>
          <MaterialIcon symbol="account_tree" weight={800} />
        </UpgradeWrapper>
      ),
      to: routes.AccessGraph.path,
      active: isActive(routes.AccessGraph.path),
      isEnabled: flags.accessGraph.isEnabled,
      hasPermission: permissions.accessGraph,
      dataTrigger: CustomAnalyticsEvents.ACCESS_GRAPH_SIDEBAR_MENU_CLICKED,
    },
    {
      label: 'Anomalies',
      icon: flags.alerts.isPremium ? (
        <UpgradeWrapper isPremium={flags.alerts.isPremium} withPopover={false}>
          <MaterialIcon symbol="troubleshoot" weight={800} />
        </UpgradeWrapper>
      ) : (
        <NewBadge icon={<MaterialIcon symbol="troubleshoot" weight={800} />} />
      ),
      to: `${routes.AccessAlerts.path}?${QueryParams.period}=30-days`,
      active: isActive(routes.AccessAlerts.path),
      isEnabled: flags.alerts.isEnabled,
      hasPermission: permissions.anomalies,
      dataTrigger: CustomAnalyticsEvents.ACCESS_ANOMALIES_SIDEBAR_MENU_CLICKED,
    },
    {
      label: 'JIT Protect',
      icon: <MaterialIcon symbol="security" weight={800} />,
      to: routes.StandingAccessReports.path,
      active: isActive(routes.StandingAccessReports.path),
      isEnabled: flags.assessmentReports.isEnabled,
      hasPermission: permissions.standingAccess,
    },
  ].filter((item) => item.isEnabled && item.hasPermission);

  const accessManagementItems: SidebarItem[] = [
    {
      label: 'Access Flows',
      icon: <MaterialIcon symbol="lock" weight={800} />,
      to: routes.AccessFlows.path,
      active: isActive(routes.AccessFlows.path),
      hasPermission: permissions.accessFlow,
      isEnabled: true,
    },
    {
      label: 'Bundles',
      icon: (
        <UpgradeWrapper isPremium={flags.bundles.isPremium} withPopover={false}>
          <MaterialIcon symbol="stacks" weight={800} />
        </UpgradeWrapper>
      ),
      to: routes.Bundles.path,
      active: isActive(routes.Bundles.path),
      isEnabled: flags.bundles.isEnabled,
      hasPermission: permissions.accessBundle,
    },
  ].filter((item) => item.isEnabled && item.hasPermission);

  const environmentItems: SidebarItem[] = [
    {
      label: 'Integrations',
      icon: permissions.integrations ? <IntegrationsErrorBadge /> : <MaterialIcon symbol="cable" weight={800} />,
      to: routes.ConnectedIntegrations.path,
      active: isActive([routes.ConnectedIntegrations.path, routes.Catalog.path]),
      hasPermission: permissions.integrations,
      isEnabled: true,
    },
    {
      label: 'Connectors',
      icon: <MaterialIcon symbol="electrical_services" weight={800} />,
      to: routes.Connectors.path,
      active: isActive(routes.Connectors.path),
      hasPermission: permissions.connectors,
      isEnabled: true,
    },
    {
      label: 'Identities',
      icon: <MaterialIcon symbol="group" weight={800} />,
      to: routes.Identities.path,
      active: isActive(routes.Identities.path),
      hasPermission: permissions.identities,
      isEnabled: true,
    },
    {
      label: 'Inventory',
      icon: <NewBadge icon={<MaterialIcon symbol="data_table" weight={800} />} />,
      to: routes.Inventory.path,
      active: isActive(routes.Inventory.path),
      isEnabled: flags.inventory.isEnabled,
      hasPermission: permissions.inventory,
      dataTrigger: CustomAnalyticsEvents.INVENTORY_SIDEBAR_MENU_CLICKED,
    },
  ].filter((item) => item.isEnabled && item.hasPermission);

  const administrationItems: SidebarItem[] = [
    {
      label: 'Activity',
      icon: <MaterialIcon symbol="overview" weight={800} />,
      to: routes.Activity.path,
      active: isActive(routes.Activity.path),
      hasPermission: permissions.activity,
      isEnabled: true,
    },
    {
      label: 'Reports',
      icon: <MaterialIcon symbol="list" weight={800} />,
      to: routes.Reports.path,
      active: isActive(routes.Reports.path),
      hasPermission: permissions.reports,
      isEnabled: true,
    },
    {
      label: routes.AuditEvents.label,
      icon: (
        <UpgradeWrapper isPremium={flags.sessionAudit.isPremium} withPopover={false}>
          <MaterialIcon symbol="quick_reference_all" weight={800} />
        </UpgradeWrapper>
      ),
      to: routes.AuditEvents.path,
      active: isActive(routes.AuditEvents.path),
      isEnabled: flags.sessionAudit.isEnabled,
      hasPermission: permissions.sessionAudit,
      dataTrigger: CustomAnalyticsEvents.SESSION_AUDIT_SIDEBAR_MENU_CLICKED,
    },
    {
      label: 'Audit Log',
      icon: <MaterialIcon symbol="browse_activity" weight={800} />,
      to: routes.SystemLog.path,
      active: isActive(routes.SystemLog.path),
      isEnabled: flags.systemLog.isEnabled,
      hasPermission: permissions.syslog,
    },
    {
      label: 'Webhooks',
      icon: <MaterialIcon symbol="webhook" weight={800} />,
      to: routes.Webhooks.path,
      active: isActive(routes.Webhooks.path),
      isEnabled: flags.webhooks.isEnabled,
      hasPermission: permissions.webhook,
    },
  ].filter((item) => item.isEnabled && item.hasPermission);

  const renderSidebarItems = (items: SidebarItem[]) =>
    items.map((item) => (
      <AppSidebarItem
        key={item.label}
        enableAnimation
        label={item.label}
        minimized={minimized}
        icon={item.icon}
        to={item.to}
        active={item.active}
        data-trigger={item.dataTrigger}
      />
    ));

  return (
    <Stack direction="column">
      {permissions.search && <NavBarSearch minimized={minimized} setMinimized={setMinimized} />}

      {overviewItems.length > 0 && (
        <>
          <DividerText text="Overview" minimized={minimized} />
          {renderSidebarItems(overviewItems)}
        </>
      )}

      {accessManagementItems.length > 0 && (
        <>
          <DividerText text="Access Management" minimized={minimized} />
          {renderSidebarItems(accessManagementItems)}
        </>
      )}

      {environmentItems.length > 0 && (
        <>
          <DividerText text="Environment" minimized={minimized} />
          {renderSidebarItems(environmentItems)}
        </>
      )}

      {administrationItems.length > 0 && (
        <>
          <DividerText text="Administration" minimized={minimized} />
          {renderSidebarItems(administrationItems)}
        </>
      )}
    </Stack>
  );
}

function IntegrationsErrorBadge() {
  const { aggregated_status } = useIntegrationCatalogStatuses();
  const integrationsErrorTotal = aggregated_status.find((r) => r.status == 'Error')?.total || 0;
  return <SidebarBadge icon={<MaterialIcon symbol="cable" weight={800} />} content={integrationsErrorTotal} />;
}
